/** The Intercom Messenger, booted on demand (spec §11.5). Nothing about
 *  Intercom loads before the user opens the chat with a valid consent:
 *  the SDK is imported dynamically here and nowhere else
 *  (noEagerIntercom.test.ts), and ``GET /api/intercom/messenger`` (404
 *  unless the org has support chat on, the provider is fully configured
 *  and the user consented) supplies the identity.
 *
 *  While booted, the boot endpoint is polled every 5 minutes. A 404 shuts
 *  the Messenger down; a fresh answer carries a new identity token (the
 *  JWT lives 1 hour), which goes into the Messenger through ``update``
 *  ("send a JWT for every request where you are … trying to update data
 *  about them", Intercom's JWT article) before the old one expires; and a
 *  different company (the user switched org) re-boots it for that org. */
import * as React from "react";

import { api } from "@/api/client";
import { clearIntercomStorage, knownAppId, rememberAppId } from "@/features/support/intercomCleanup";

type Boot = {
  app_id: string;
  api_base: string;
  user_id: string;
  name: string;
  email: string;
  created_at: number;
  company: { company_id: string; name: string };
  intercom_user_jwt: string;
  language_override: string;
};
type Region = "us" | "eu" | "ap";
/** The named exports of ``@intercom/messenger-js-sdk`` 0.0.20 we use. Its
 *  default export sets ``window.intercomSettings`` and injects the widget
 *  script; ``boot`` re-initialises after a ``shutdown``. */
type Sdk = {
  default: (settings: Record<string, unknown>) => void;
  boot: (settings: Record<string, unknown>) => void;
  show: () => void;
  shutdown: () => void;
  update: (settings: Record<string, unknown>) => void;
};

/** How often the boot endpoint is re-checked while booted. */
export const POLL_MS = 5 * 60 * 1000;
/** A token this old is replaced on the next poll (it expires at 60 min). */
export const JWT_REFRESH_MS = 45 * 60 * 1000;
/** At most one route update per 2 minutes (facts §1.4: 20 per 30 min). */
const ROUTE_UPDATE_MS = 120_000;

let sdk: Sdk | null = null;
let scriptBooted = false;
let appId: string | null = null;
let companyId: string | null = null;
let jwtAt = 0;
let poll: number | null = null;
let lastUpdate = 0;
/** Bumped by every shutdown (logout, withdrawal, add-on off): an openChat
 *  or a poll whose awaits span one must not boot the Messenger again. An
 *  org-switch re-boot is not a shutdown and leaves it alone, so a click
 *  pending meanwhile still opens the chat. */
let generation = 0;
/** The storage wipe a shutdown schedules for the next tick: cancelled by
 *  the next boot, so it never removes the new session's cookies. */
let pendingWipe: number | null = null;
/** Bumped by every boot: an openChat that finds the Messenger re-booted
 *  during its awaits (an org switch) shows it as it is now. */
let bootSeq = 0;

/** The SDK derives ``api_base`` from ``region`` (default "us") and
 *  overwrites any ``api_base`` passed in, so the backend's host is mapped
 *  back to its region. */
function regionOf(apiBase: string): Region {
  if (apiBase.includes(".eu.")) return "eu";
  if (apiBase.includes(".au.")) return "ap";
  return "us";
}

function intercomWindow(): { intercomSettings?: Record<string, unknown> } {
  return window as unknown as { intercomSettings?: Record<string, unknown> };
}

async function loadSdk(): Promise<Sdk> {
  if (!sdk) sdk = (await import("@intercom/messenger-js-sdk")) as unknown as Sdk;
  return sdk;
}

function bootWith(s: Sdk, boot: Boot): void {
  if (pendingWipe !== null) {
    window.clearTimeout(pendingWipe);
    pendingWipe = null;
  }
  const settings = { ...boot, hide_default_launcher: true };
  if (scriptBooted) {
    s.boot(settings);
  } else {
    s.default({ ...settings, region: regionOf(boot.api_base) });
    scriptBooted = true;
  }
  // Belt and braces: the widget script reads this when it loads.
  const w = intercomWindow();
  if (w.intercomSettings) w.intercomSettings["api_base"] = boot.api_base;
  appId = boot.app_id;
  rememberAppId(boot.app_id);
  companyId = boot.company.company_id;
  bootSeq += 1;
  jwtAt = Date.now();
  if (poll === null) poll = window.setInterval(() => void recheck(), POLL_MS);
}

/** Another org is active: end the old org's session and boot with the
 *  new company. Same user, so nothing is wiped and the open/poll guards
 *  are not tripped. */
function switchCompany(s: Sdk, boot: Boot): void {
  s.shutdown();
  appId = null;
  companyId = null;
  bootWith(s, boot);
}

/** Boot (first time) or re-boot (after a shutdown) and show the chat. */
export async function openChat(): Promise<void> {
  const started = generation;
  const seq = bootSeq;
  const boot = (await api.get<Boot>("/intercom/messenger")).data;
  const s = await loadSdk();
  if (generation !== started) return; // shut down meanwhile: stay down
  if (appId === null) bootWith(s, boot);
  // Booted for another org (the user switched since): this answer is the
  // current one, unless a poll re-booted it during our awaits, whose
  // answer is newer. Either way the click still opens the chat.
  else if (bootSeq === seq && companyId !== boot.company.company_id) switchCompany(s, boot);
  s.show();
}

/** Re-check the boot endpoint now (the poll, and an org switch). */
export async function recheck(): Promise<void> {
  if (appId === null) return;
  const started = generation;
  let fresh: Boot;
  try {
    fresh = (await api.get<Boot>("/intercom/messenger")).data;
  } catch {
    if (generation === started) shutdownIntercom();
    return;
  }
  if (generation !== started || appId === null || !sdk) return;
  if (fresh.company.company_id !== companyId) {
    // Another org is active now: its company, not the old one, gets the
    // conversations and the pings. Re-booted quietly, not shown.
    switchCompany(sdk, fresh);
    return;
  }
  const now = Date.now();
  if (now - jwtAt >= JWT_REFRESH_MS) {
    sdk.update({ intercom_user_jwt: fresh.intercom_user_jwt });
    jwtAt = now;
    lastUpdate = now;
  }
}

/** End the session and remove what Intercom stored in the browser. Safe
 *  to call when the Messenger never loaded. */
export function shutdownIntercom(): void {
  generation += 1;
  if (poll !== null) window.clearInterval(poll);
  poll = null;
  if (sdk && appId !== null) sdk.shutdown();
  // This tab's Messenger, else one booted here before (an earlier tab or
  // load); never booted in this browser: nothing to clear.
  const id = appId ?? knownAppId();
  appId = null;
  companyId = null;
  // The settings carry the user's email and token.
  delete intercomWindow().intercomSettings;
  clearIntercomStorage(id);
  // A widget script still loading may write its cookies after this; clear
  // once more on the next tick, unless a new boot comes first.
  if (pendingWipe !== null) window.clearTimeout(pendingWipe);
  pendingWipe = window.setTimeout(() => {
    pendingWipe = null;
    clearIntercomStorage(id);
  }, 0);
}

/** At most one update per 2 minutes (facts §1.4: 20 per 30 minutes). */
export function useIntercomRouteUpdates(pathname: string): void {
  React.useEffect(() => {
    if (!sdk || appId === null) return;
    const now = Date.now();
    if (now - lastUpdate < ROUTE_UPDATE_MS) return;
    lastUpdate = now;
    sdk.update({ last_request_at: Math.floor(now / 1000) });
  }, [pathname]);
}
